import { jsx } from "panel";
import camelCase from "lodash/camelCase";
import {
  Action,
  EntryPart,
  FiltersOperator
} from "common/query-builder/query-entry-section/types";
import { ButtonEl } from "common/components/button";
import { ButtonTheme } from "common/components/button/constants";
import { QueryBlockEl } from "common/query-builder/query-block";
import {
  MenuItemType,
  TypecastPillAction
} from "common/query-builder/query-block/properties-menu/types";
import { RowFilters, RowFiltersPrefix } from "common/query-builder/query-entry-section/components/filters";
import { serializeAttributes } from "common/util";
import { ResourceType } from "common/report/constants";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { SUPPORTED_BEHAVIORAL_AGGREGATIONS } from "common/report/util/cohorts/utils";
import { QueryEntrySectionEl } from "../base/query-entry-section-el";
import { BaseEntryRowEl } from "../base/template";
import { isBehavioralFilterNegated, transformBehavioralFilterToBehavioralEntry } from "../behavior/utils";
import { BehavioralAction } from "./types";
export function CohortGroupEntryRowEl({ index, entry, section }) {
  const hasFilters = this.entry.filters.length > 0;
  const isStagingFilter = Boolean(this.section.state.placeholders.get(this.index));
  const hasExtendedOperators = this.showExtendedLogicalOperators(`filtersOperator`) || this.showExtendedLogicalOperators(`behavioralFiltersOperator`);
  const filterQueryBlockConfig = {
    elref: `behavioralFilterBlock`,
    type: `properties`,
    theme: ButtonTheme.Tertiary,
    "allow-typecast-pill-action": TypecastPillAction.TriggerTutorial,
    "selector-icon": `plus`,
    "hide-edit-controls": section.attr(`hide-edit-controls`),
    "data-group-id": section.attr(`data-group-id`),
    "enable-enhanced-list-filters": section.attr(`enable-enhanced-list-filters`),
    "enable-workspace-filter-restrictions": section.attr(`enable-workspace-filter-restrictions`),
    "exclude-custom-events": section.attr(`exclude-custom-events`),
    "inline-cohorts-override": section.attr(`inline-cohorts-override`),
    "project-id": section.attr(`project-id`),
    "input-only": true,
    "show-special-dataset-property": section.attr(`show-special-dataset-property`),
    "hide-special-date-property-unit": section.attr(`hide-special-date-property-unit`),
    "show-special-date-property": section.attr(`show-special-date-property`),
    "track-name": section.attr(`track-name`),
    "selector-type": `button`,
    "user-id": section.attr(`user-id`),
    disabled: section.attr(`disabled`),
    "workspace-id": section.attr(`workspace-id`),
    "show-all-tab": true,
    "hide-events": true,
    "menu-item-types": [
      MenuItemType.Events,
      MenuItemType.UserProperties,
      MenuItemType.UserCustomProperties,
      MenuItemType.LookupTableProperties,
      MenuItemType.Cohorts
    ],
    "all-data-groups": section.attr(`data-groups-enabled`),
    "exclude-dimension-properties": section.attr(`exclude-dimension-properties`),
    "include-custom-properties": section.attr(`include-custom-properties`),
    "project-has-cohorts": section.attr(`project-has-cohorts`),
    "project-has-people": section.attr(`project-has-people`),
    "support-b2b-cohorts": section.attr(`support-b2b-cohorts`)
  };
  const selectorRenderer = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry-selector-title"
  }, this.getGroupTitle()), !isStagingFilter && !hasFilters && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(QueryBlockEl, {
    key: "entry-user-filter-selector",
    attrs: {
      ...filterQueryBlockConfig,
      label: `Filter`,
      autofocus: this.section.state.stagedRows.length === 1 && this.index === 0
    },
    props: { contextPanelParentElement: section.helpers.getContextPanelParentEl(index) },
    on: {
      select: (ev) => this.handlePropertyItemClick(ev)
    }
  })));
  const bodyRowRenderer = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(RowFilters, {
    el: section,
    entry,
    event: entry.event,
    index,
    handleUpdate: (_filters, filtersOperator, _queryReason, ev) => {
      if (filtersOperator && !ev) {
        section.updateEntries(Action.Edit, { entry: { ...entry, filtersOperator }, index });
        return;
      }
      this.handleFilterSectionUpdate(EntryPart.Filters, ev.detail);
    },
    filtersOperators: this.showExtendedLogicalOperators(`filtersOperator`) && [
      FiltersOperator.And,
      FiltersOperator.Or,
      FiltersOperator.AndAny,
      FiltersOperator.OrAll
    ]
  }), BehavioralFrequencyFilters(this), hasFilters && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry.is-add-row",
    class: { "is-disabled": this.isPlaceholder() }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".entry-selector-prefix",
    class: { "for-extended-operators": hasExtendedOperators }
  }), /* @__PURE__ */ jsx(QueryBlockEl, {
    key: "entry-filter-selector",
    attrs: {
      ...filterQueryBlockConfig,
      label: `Filter`
    },
    props: { contextPanelParentElement: section.helpers.getContextPanelParentEl(index) },
    on: {
      select: (ev) => this.handlePropertyItemClick(ev)
    }
  }))));
  const groupingControl = entry.groupingOperator && /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry-group-operator",
    class: { [`is-${entry.groupingOperator}`]: true }
  }, /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      elref: `entryGroupOperator`,
      theme: entry.groupingOperator === FiltersOperator.And ? ButtonTheme.Primary : ButtonTheme.Tertiary,
      role: `button`
    },
    on: {
      click: () => this.handleEntryGroupingOperatorClick()
    }
  }, entry.groupingOperator));
  return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(BaseEntryRowEl, {
    index,
    el: section,
    entry,
    rowRenderer: { prefixRenderer: null, selectorRenderer, bodyRowRenderer },
    hideActions: true,
    isPlaceholder: this.isPlaceholder()
  }), groupingControl);
}
function BehavioralFrequencyFilters(row) {
  var _a, _b;
  const { placeholders } = row.section.state;
  const showPlaceholder = (_a = placeholders.get(row.index)) == null ? void 0 : _a.includes(EntryPart.BehavioralFrequencyFilters);
  const filters = row.section.helpers.getEntryFilters(row.index)[EntryPart.BehavioralFrequencyFilters] || [];
  if (filters.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(QueryEntrySectionEl, {
    key: `entry-${EntryPart.BehavioralFrequencyFilters}-section`,
    class: { "indent-filters": true },
    attrs: serializeAttributes({
      ...row.section.helpers.getNestedSectionAttrs(),
      elref: camelCase(`entry-${EntryPart.BehavioralFrequencyFilters}-section`),
      "all-data-groups": row.section.attr(`data-groups-enabled`),
      "data-groups-enabled": row.section.attr(`data-groups-enabled`),
      "allow-cohort-filter": false,
      "can-rename-step": false,
      "project-has-people": row.section.attr(`project-has-people`),
      "project-has-cohorts": false,
      "disable-add-entry": true,
      "row-label-type": `none`,
      "metrics-mode": row.entry.behavioralFiltersOperator !== FiltersOperator.Then,
      "include-custom-properties": `all`,
      "hide-edit-controls": row.section.attr(`hide-edit-controls`),
      "row-types": [
        {
          value: ResourceType.Event,
          includeDateRange: true
        }
      ],
      entries: filters.map(
        (filter) => transformBehavioralFilterToBehavioralEntry(filter, row.entry.behavioralFiltersOperator)
      ),
      "event-aggregator-functions": SUPPORTED_BEHAVIORAL_AGGREGATIONS,
      "property-aggregator-functions": SUPPORTED_BEHAVIORAL_AGGREGATIONS,
      "track-properties": { sectionName: `inline-cohort` }
    }),
    props: {
      prefixRenderer: ({ index: filterIndex, entry }) => BehavioralRowFiltersPrefix({
        filterIndex,
        filter: filters[filterIndex],
        filtersOperator: row.entry.behavioralFiltersOperator,
        handleChange: (operator, action) => row.handleBehavioralFilterOperatorChange({
          operator,
          action,
          entry,
          filterIndex
        }),
        isReadOnlyMode: row.section.attr(`readonly`),
        includeExtendedOperators: row.showExtendedLogicalOperators(`behavioralFiltersOperator`) && filters.length >= 3,
        isNegated: isBehavioralFilterNegated(entry)
      }),
      isStagingRow: showPlaceholder && {
        ...placeholders.get(row.index)[1],
        type: ResourceType.Event,
        behavioralFiltersOperator: ((_b = row.entry) == null ? void 0 : _b.behavioralFiltersOperator) || FiltersOperator.And
      }
    },
    on: {
      abortStage: () => row.handleAbortStage(),
      update: (ev) => row.handleFilterSectionUpdate(EntryPart.BehavioralFrequencyFilters, ev.detail)
    }
  });
}
export function BehavioralRowFiltersPrefix({
  filtersOperator,
  filter,
  filterIndex,
  handleChange,
  isReadOnlyMode,
  includeExtendedOperators,
  isNegated
}) {
  return /* @__PURE__ */ jsx(null, null, RowFiltersPrefix({
    filtersOperator,
    filter,
    filterIndex,
    handleChange: (item) => handleChange(item.value, null),
    isReadOnlyMode,
    forBehaviors: true,
    filtersOperators: includeExtendedOperators ? [
      FiltersOperator.And,
      FiltersOperator.Or,
      FiltersOperator.AndAny,
      FiltersOperator.OrAll,
      FiltersOperator.Then
    ] : [FiltersOperator.And, FiltersOperator.Or, FiltersOperator.Then]
  }), /* @__PURE__ */ jsx(SelectEl, {
    sel: ".entry-selector-behavioral-action",
    attrs: {
      elref: `behavioralFilterActionSelect`,
      theme: SelectTheme.Select,
      "menu-width": 100,
      options: [BehavioralAction.Did, BehavioralAction.DidNot],
      "never-show-caret": true,
      "selected-option": isNegated ? BehavioralAction.DidNot : BehavioralAction.Did
    },
    on: { change: (ev) => handleChange(null, ev.detail.value) }
  }));
}
